import type { LaunchInventoryItem } from "./launchpad";
import type { OrdinalOutputRecord } from "./types";

const INSCRIPTION_ID_PATTERN = /^[0-9a-f]{64}i\d+$/i;
const PREFILL_MAX_AGE_MS = 24 * 60 * 60_000;

export const HISTORY_LAUNCHPAD_PREFILL_KEY = "spam-the-chain:launchpad-history-prefill:v1";

export interface HistoryLaunchpadPrefill {
  version: 1;
  paymentAddress: string;
  inscriptionIds: string[];
  createdAt: number;
}

export interface ResolvedHistoryLaunchpadInventory {
  inventory: LaunchInventoryItem[];
  missingIds: string[];
}

export function createHistoryLaunchpadPrefill(
  paymentAddress: string,
  inscriptionIds: Iterable<string>,
  createdAt = Date.now(),
): HistoryLaunchpadPrefill {
  const normalized = [...new Set([...inscriptionIds].map((id) => id.trim().toLowerCase()))]
    .filter((id) => INSCRIPTION_ID_PATTERN.test(id))
    .slice(0, 10_000);
  if (!paymentAddress.trim()) throw new Error("Connect the wallet that created these inscriptions");
  if (normalized.length === 0) throw new Error("Select at least one inscription");
  return { version: 1, paymentAddress: paymentAddress.trim(), inscriptionIds: normalized, createdAt };
}

export function parseHistoryLaunchpadPrefill(
  raw: string | null,
  paymentAddress: string,
  now = Date.now(),
): HistoryLaunchpadPrefill | null {
  try {
    const value = JSON.parse(raw ?? "null") as Partial<HistoryLaunchpadPrefill> | null;
    if (
      value?.version !== 1
      || typeof value.paymentAddress !== "string"
      || value.paymentAddress.toLowerCase() !== paymentAddress.toLowerCase()
      || !Array.isArray(value.inscriptionIds)
      || value.inscriptionIds.length < 1
      || value.inscriptionIds.length > 10_000
      || value.inscriptionIds.some((id) => typeof id !== "string" || !INSCRIPTION_ID_PATTERN.test(id))
      || typeof value.createdAt !== "number"
      || value.createdAt > now + 60_000
      || now - value.createdAt > PREFILL_MAX_AGE_MS
    ) return null;
    return {
      version: 1,
      paymentAddress: value.paymentAddress,
      inscriptionIds: [...new Set(value.inscriptionIds.map((id) => id.toLowerCase()))],
      createdAt: value.createdAt,
    };
  } catch {
    return null;
  }
}

export function resolveHistoryLaunchpadInventory(
  inscriptionIds: readonly string[],
  outputs: readonly OrdinalOutputRecord[],
): ResolvedHistoryLaunchpadInventory {
  const available = new Map<string, LaunchInventoryItem>();
  for (const output of outputs) {
    if (output.spent === true || output.inscriptions?.length !== 1 || Object.keys(output.runes ?? {}).length > 0) continue;
    const inscriptionId = output.inscriptions[0]?.toLowerCase();
    if (!inscriptionId || !INSCRIPTION_ID_PATTERN.test(inscriptionId) || available.has(inscriptionId)) continue;
    available.set(inscriptionId, { inscriptionId, outpoint: output.outpoint, value: output.value });
  }

  const inventory: LaunchInventoryItem[] = [];
  const missingIds: string[] = [];
  for (const inscriptionId of [...new Set(inscriptionIds.map((id) => id.toLowerCase()))]) {
    const item = available.get(inscriptionId);
    if (item) inventory.push(item);
    else missingIds.push(inscriptionId);
  }
  return { inventory, missingIds };
}
